import React, { ChangeEvent, FC, useContext, useState } from "react";
import TableComponent, { IRow } from "components/tableComponent";
import { dateIsPastOrToday, prettifyOrgNumber } from "utils/helpers";
import { IMembership } from "reducers/memberships";
import { format, parseISO } from "date-fns";
import { getActiveStatus } from "utils/ecommerseTypes";
import { dateFormats } from "utils/formats";
import { THeadCell } from "utils/types";
import { LocalizeText } from "components/localizer";
import { Box, FormControlLabel, Switch } from "@mui/material";
import StyledSwitchContainer from "./switchContainer";
import { IUsePaginationStore } from "utils/usePaginationStore";
import { MembershipsContext } from "contexts/memberships";

const headCells: THeadCell[] = [
    {
        id: "name",
        sortId: "organization__name",
        label: <LocalizeText tag="commercialCompanyNameShort" />,
    },
    {
        id: "org_nr",
        sortId: "organization__org_nr",
        label: <LocalizeText tag="orgNumberShort" />,
    },
    {
        id: "agreement_nr",
        sortId: "agreement_nr",
        label: <LocalizeText tag="customerNumberShort" />,
    },
    { id: "start_date", sortId: "start_date", label: <LocalizeText tag="from" /> },
    { id: "end_date", sortId: "end_date", label: <LocalizeText tag="statusMembership" /> },
    { id: "", label: "", alignCell: "right" },
];

interface IMembershipTableProps {
    pagination: IUsePaginationStore<"memberships">;
    onClick?: (membership: IMembership) => void;
    forceLoadingBehaviour?: boolean;
}

const MembershipTable: FC<IMembershipTableProps> = ({ pagination, onClick, forceLoadingBehaviour }) => {
    const { isFetchingMemberships } = useContext(MembershipsContext);
    const [inclTerminatedFilter, setInclTerminatedFilter] = useState<boolean>(false);

    const onIncludeTerminatedMemberships = (event: ChangeEvent<HTMLInputElement>): void => {
        pagination.setParam("is_active", event.target.checked ? null : false);
        setInclTerminatedFilter(event.target.checked);
    };

    const mapRows = (memberships: IMembership[]): IRow[] =>
        memberships.map((membership, index) => {
            const getDateIsPastOrToday = membership.end_date ? dateIsPastOrToday(parseISO(membership.end_date ?? "")) : false;
            const { organization, agreement_nr, start_date, end_date } = membership;
            const { name, org_nr } = organization;
            return {
                onClick: !!onClick ? () => onClick(membership) : undefined,
                disabled: getDateIsPastOrToday,
                data: [
                    name || "-",
                    org_nr ? prettifyOrgNumber(org_nr) : "-",
                    agreement_nr || "-",
                    start_date ? format(parseISO(start_date ?? ""), dateFormats.WEBDATE) : "",
                    getActiveStatus(end_date),
                ],
            };
        });

    const rows = mapRows(pagination.results);

    return (
        <Box>
            <StyledSwitchContainer>
                <FormControlLabel
                    control={
                        <Switch
                            checked={inclTerminatedFilter}
                            onChange={onIncludeTerminatedMemberships}
                            color="primary"
                        />
                    }
                    label={<LocalizeText tag="includeTerminatedMemberships" />}
                />
            </StyledSwitchContainer>
            <TableComponent
                headCells={headCells}
                isLoading={isFetchingMemberships || forceLoadingBehaviour}
                rows={rows}
                skeletonVariant="options"
                pagination={pagination}
            />
        </Box>
    );
};

export default MembershipTable;
